import {useEffect, useState} from 'react';

import {UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {makeEnumTypeGuard} from '../../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {useUnmountCancelToken} from '../../../../../srcCommon/helpers/hookHelpers';
import {CancelToken} from '../../../../helpers/asyncCancelHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {RuleCompoundConditions, RuleCompoundConditionTypesEnum} from '../../../../models/api/ruleModel';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {CustomFields} from '../../../editors/commons/customFieldsModel';
import {PreAtom} from '../../../editors/expressions/expressionAtomModel';
import {RuleAction} from '../../ruleActionModel';
import {RuleCondition, RuleConditionTypesEnum} from '../../ruleConditionModel';
import {CustomFieldsService} from '../../ruleEditor/services/customFieldsService';
import {RuleRecipePlaceholder, RuleRecipePlaceholderTypesEnum} from './builder';
import {RuleRecipe} from './index';

function placeholderReplacer(placeholderMap: Map<unknown, number>) {
  return <T extends RuleAction | RuleCondition | PreAtom>(item: T): T => {
    if (!('params' in item) || !item.params || !('custom_field' in item.params)) {
      return item;
    }
    const replacement = placeholderMap.get(item.params.custom_field);
    if (!replacement) {
      return item;
    }
    return {
      ...item,
      params: {
        ...item.params,
        custom_field: replacement,
      },
    };
  };
}

const isConditionOperator = makeEnumTypeGuard(RuleCompoundConditionTypesEnum);

function compoundConditionsReplacer(
  conditions: RuleCompoundConditions,
  replacer: (condition: RuleCondition) => RuleCondition,
): RuleCompoundConditions {
  return conditions.map((simpleCondition) => {
    if (isConditionOperator(simpleCondition)) {
      return simpleCondition;
    }
    if (Array.isArray(simpleCondition)) {
      return simpleCondition.map((condition) => {
        if (isConditionOperator(condition)) {
          return condition;
        }
        return replacer(condition);
      });
    }
    return replacer(simpleCondition);
    // The `as` is needed because TS is not able to deduce the tuple type from this.
  }) as RuleCompoundConditions;
}

async function findOrCreatePlaceholder(
  placeholder: RuleRecipePlaceholder,
  customFields: CustomFields,
  companyUrl: string,
  cancelToken: CancelToken,
): Promise<number> {
  switch (placeholder.type) {
    case RuleRecipePlaceholderTypesEnum.CUSTOM_FIELD: {
      // Check for existing custom fields.
      const existingCustomField = customFields[placeholder.params.resourceType].find(
        (field) => field.name === placeholder.params.name,
      );
      if (existingCustomField) {
        return existingCustomField.id;
      }

      const payload = await CustomFieldsService.createPlaceholder(placeholder, companyUrl, cancelToken);

      const {resourceType} = placeholder.params;
      // eslint-disable-next-line no-param-reassign
      customFields[resourceType] = [...customFields[resourceType], payload];

      return payload.id;
    }
    default:
      throw new UnreachableError(placeholder.type);
  }
}

export function maybeAddSelectedInboxToRecipe(recipe: RuleRecipe | undefined, inboxId: number | undefined) {
  if (!recipe || !inboxId) {
    return recipe;
  }

  return {
    ...recipe,
    rule: {
      ...recipe.rule,
      conditions: compoundConditionsReplacer(recipe.rule.conditions, (condition) => {
        if (condition.type !== RuleConditionTypesEnum.INBOX_IN) {
          return condition;
        }
        return {
          ...condition,
          params: {
            inboxes: [inboxId],
          },
        };
      }),
    },
  };
}

export function useResolveRecipePlaceholders(
  recipe: RuleRecipe | undefined,
  customFields: CustomFields | undefined,
): [boolean, RuleRecipe | undefined] {
  const shouldResolveRecipe = Boolean(recipe?.placeholders?.length);
  const companyUrl = useAppSelector(getCompanyUrl);
  const [resolvedRecipe, setResolvedRecipe] = useState<RuleRecipe>();
  const cancelToken = useUnmountCancelToken();

  useEffect(() => {
    const resolvePlaceholders = async () => {
      if (!recipe || !recipe.placeholders) {
        setResolvedRecipe(recipe);
        return;
      }

      // Don't double-resolve
      if (resolvedRecipe && resolvedRecipe.id === recipe.id) {
        return;
      }

      if (!customFields) {
        return;
      }

      const resolvedPlaceholderIds = new Map<string, number>();

      await Promise.all(
        recipe.placeholders.map(async (placeholder) => {
          // Otherwise create it
          resolvedPlaceholderIds.set(
            placeholder.id,
            await findOrCreatePlaceholder(placeholder, customFields, companyUrl, cancelToken),
          );
        }),
      );

      const replacer = placeholderReplacer(resolvedPlaceholderIds);

      setResolvedRecipe({
        ...recipe,
        placeholders: undefined,
        rule: {
          ...recipe.rule,
          actions: recipe.rule.actions.map(replacer),
          expressions: recipe.rule.expressions?.map((expression) => ({
            ...expression,
            atoms: expression.atoms.map(replacer),
          })),
          conditions: compoundConditionsReplacer(recipe.rule.conditions, replacer),
        },
      });
    };

    resolvePlaceholders();
  }, [recipe, customFields, companyUrl, cancelToken, resolvedRecipe]);

  return [shouldResolveRecipe, resolvedRecipe];
}
